import { useState } from 'react';
import { AlertTriangle, RefreshCw, Play } from 'lucide-react';
import { AnomalyFilterBar, type FilterState } from '../components/anomalies/AnomalyFilterBar';
import { AnomaliesTable } from '../components/anomalies/AnomaliesTable';
import { ScanDialog } from '../components/anomalies/ScanDialog';
import {
  useAnomalies,
  useResolveAnomalies,
  useRescanAnomalies,
  useBackfillDay,
  type ResolveAction,
  type AnomalyRow,
} from '../hooks/useAnomalies';
import type { AnomalyGroup } from '../components/anomalies/groupAnomalies';

function defaultFilter(): FilterState {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setUTCDate(today.getUTCDate() - 7);
  return {
    from: weekAgo.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
    storeId: undefined,
    platforms: [],
    severities: ['critical', 'warning', 'info'],
    status: 'open',
  };
}

export function AnomaliesPage() {
  const [filter, setFilter] = useState<FilterState>(defaultFilter());
  const [scanOpen, setScanOpen] = useState(false);
  const { data, isLoading, error, refetch } = useAnomalies({
    from: filter.from,
    to: filter.to,
    storeId: filter.storeId,
    severities: filter.severities,
    status: filter.status,
    limit: 200,
  });
  const resolve = useResolveAnomalies();
  const rescan = useRescanAnomalies();
  const backfill = useBackfillDay();
  const [repullPending, setRepullPending] = useState<Set<string>>(new Set());
  const [rescanPending, setRescanPending] = useState<Set<string>>(new Set());

  function handleResolve(rows: AnomalyRow[], action: ResolveAction) {
    resolve.mutate({
      anomalyKeys: rows.map((r) => ({
        storeId: r.storeId,
        platform: r.platform,
        metric: r.metric,
        date: r.date,
      })),
      action,
    });
  }

  function handleRepull(group: AnomalyGroup) {
    setRepullPending((prev) => {
      const next = new Set(prev);
      next.add(group.key);
      return next;
    });
    backfill.mutate(
      { storeId: group.storeId, platform: group.platform, date: group.date },
      {
        onSuccess: () => {
          window.alert('Backfill enqueued — give it a couple of minutes, then click Rescan.');
        },
        onError: (err) => {
          window.alert(`Backfill failed: ${(err as Error).message}`);
        },
        onSettled: () => {
          setRepullPending((prev) => {
            const next = new Set(prev);
            next.delete(group.key);
            return next;
          });
        },
      },
    );
  }

  function handleRescan(group: AnomalyGroup) {
    setRescanPending((prev) => {
      const next = new Set(prev);
      next.add(group.key);
      return next;
    });
    rescan.mutate(
      { mode: 'single', storeId: group.storeId, date: group.date },
      {
        onSettled: () => {
          setRescanPending((prev) => {
            const next = new Set(prev);
            next.delete(group.key);
            return next;
          });
        },
      },
    );
  }

  return (
    <div>
      <ScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Data Anomalies
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Per-platform, per-metric daily anomalies detected against a 30-day baseline.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setScanOpen(true)}
            className="text-xs px-3 py-1.5 rounded bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 inline-flex items-center gap-1"
          >
            <Play className="w-3.5 h-3.5" /> Run scan
          </button>
          <button
            onClick={() => refetch()}
            className="text-xs px-3 py-1.5 rounded border inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <AnomalyFilterBar value={filter} onChange={setFilter} />

      {data && (
        <div className="grid grid-cols-3 gap-3 my-3">
          <SummaryCell label="Critical" count={data.summary.critical} tone="red" />
          <SummaryCell label="Warning" count={data.summary.warning} tone="amber" />
          <SummaryCell label="Info" count={data.summary.info} tone="blue" />
        </div>
      )}

      {error && <div className="text-red-700 bg-red-50 p-3 rounded">Failed to load anomalies.</div>}
      {isLoading && <div className="text-gray-500 text-sm">Loading…</div>}
      {data && (
        <AnomaliesTable
          rows={data.anomalies}
          onResolve={handleResolve}
          onRepull={handleRepull}
          onRescan={handleRescan}
          repullPendingKeys={repullPending}
          rescanPendingKeys={rescanPending}
        />
      )}
    </div>
  );
}

function SummaryCell({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'red' | 'amber' | 'blue';
}) {
  const cls =
    tone === 'red'
      ? 'bg-red-50 border-red-200 text-red-700'
      : tone === 'amber'
        ? 'bg-amber-50 border-amber-200 text-amber-700'
        : 'bg-blue-50 border-blue-200 text-blue-700';
  return (
    <div className={`border rounded-xl p-4 ${cls}`}>
      <div className="text-xs uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-2xl font-semibold">{count}</div>
    </div>
  );
}
